import React, { useMemo } from "react";
import cx from "classnames";
import { styled } from "@linaria/react";
import ChartCard from "@ui/Highcharts/ui/Card";
import { color } from "@ui";
import t from "../../../../i18n";
import InfoTooltip from "../../Chart/InfoTooltip";
import Selector from "@ui/Selector";
import PeriodSelector from "../../Analysis/PeriodSelector";
import type { ActiveAnalysis } from "../../../../api/useActiveAnalysesStream";
import type { Project } from "../../../../api/useProjects";
import type { Source } from "../../../../api/useSources";
import type { Setup } from "../../../../api/useSetups";
import useSelectedAnalyses from "../../Analysis/useSelectedAnalyses";
import { NO_FAILURES_SERIE_ID } from "./getNoFailureSeries";
import { INTERNAL_GAP_ID } from "../../Chart/series/getInternalGapSerie";
import { CONFIDENCE_AREA_ID } from "../../Chart/series/getConfidenceAreaSerie";
import { useCanUserDoOnProject } from "../../../canUserDo";
import ActionMenu from "../../Analysis/AnalysisActionMenu";
import { commonLegendactiveSetup } from "../../Chart/Legend/CommonLegend";
import useModal from "@ui/lib/hooks/useModal";
import type { IndexedFailureValues } from "../../../../api/useIndexedFailureValues";
import type { IndexedManualFailureValues } from "../../../../api/useManualFailureValues";
import { spacings } from "@ui/Theme";
import { usePublicationFlagDisplay } from "../../Analysis/TogglePublicationDisplay";
import { useSettings } from "../../../../api/useSettings";
import { FullViewportButtonMarker, FullViewportTrigger } from "@ui/FullViewportButton";
import { css } from "@linaria/core";
import { GLOBAL_DIAGNOSIS_CHART, useDiagnosisChartType } from "./hooks/useDiagnosisChartType";
import PeriodMenuDiagnosis from "./components/PeriodMenuDiagnosis";
import type { IndexedFailureMetadata } from "../../../../api/useFailureMetadata";
import {
  ExportWidgetButton,
  exportWidgetTarget,
  HIDE_ON_WIDGET_EXPORT,
} from "@ui/ExportWidgetButton";
import { type Quantity } from "../../../../api/useQuantities";
import { getAnalysisSetup } from "diagnostics-domain/setup/getSetup";
import Text from "@ui/Text";
import { generateExportFileName } from "diagnostics-lib/string";
import { UNEXPLAINED_GAP_ID } from "../../Chart/series/serieIds";
import useActiveAnalysesWithDisplayFilters from "../../../../api/useActiveAnalysesWithDisplayFilters";
import ManualFailuresModalForm from "./ManualFailuresModalForm";
import Modal from "@ui/Modal";
import DiagnosisHighcharts from "./DiagnosisHighcharts";
import { useProjectPeriod } from "../../useProjectPeriod";
import { useHasDiagnosticDataInPeriod } from "../../useNoDataIntervals";
import PeriodZoomAllButton from "../../Chart/PeriodZoomAllButton";
import EvolutionSubHeader from "../../EvolutionSubHeader";
import { DisplayFiltersBlankState } from "../../EvolutionSubHeader/DisplayFiltersBlankState";

interface P {
  project: Project;
  source: Source;
  quantities: Quantity[];
  setups: Setup[];
  // activeAnalyses: NonEmptyArray<ActiveAnalysis>;
  activeAnalyses: ActiveAnalysis[];
  indexedFailureMetadata: IndexedFailureMetadata;
  indexedFailureValues: IndexedFailureValues;
  indexedManualFailureValues: IndexedManualFailureValues;
}

export const Bullet = styled.div`
  display: inline-block;
  background-color: ${color.lightNitrogen500_Main};
  width: 4px;
  height: 4px;
  border-radius: 50%;
  margin-left: 4px;
  margin-bottom: 8px;
`;
const StyledModal = styled(Modal)`
  max-width: 1000px;
`;

export function getOtherSerieDescription({
  serieId,
  chartType,
}: {
  serieId: string | undefined;
  chartType: string;
  suspectedFailuresIncluded: boolean;
}) {
  switch (serieId) {
    case NO_FAILURES_SERIE_ID:
      return t("diagnosis.overview.legend.tooltip.no_failure");
    case INTERNAL_GAP_ID:
      return chartType === GLOBAL_DIAGNOSIS_CHART
        ? t("diagnosis.overview.legend.tooltip.internal_gap.global")
        : t("diagnosis.overview.legend.tooltip.internal_losses");
    case UNEXPLAINED_GAP_ID:
      return chartType === GLOBAL_DIAGNOSIS_CHART
        ? t("diagnosis.overview.legend.tooltip.unexplained_gap.global")
        : t("diagnosis.overview.legend.tooltip.unexplained_losses");
    case CONFIDENCE_AREA_ID:
      return t("diagnosis.overview.legend.tooltip.confidence_area");
    default:
      throw new Error("It should not happen");
  }
}

const FailureEvolutionChartCard = css`
  row-gap: ${spacings.m};
  :not(.${FullViewportButtonMarker}) {
    max-height: 80vh;
  }
`;

const FailureEvolution = ({
  project,
  quantities,
  setups,
  activeAnalyses,
  indexedFailureMetadata,
  indexedFailureValues,
  indexedManualFailureValues,
  source,
}: P) => {
  const settings = useSettings();
  const { modal, openModal, closeModal } = useModal();

  const canUserWriteProject = useCanUserDoOnProject("PROJECT_WRITE", project.id);
  const canUserReadProject = useCanUserDoOnProject("PROJECT_READ", project.id);
  const { selectedAnalyses, boundary } = useSelectedAnalyses(project.id, activeAnalyses);
  const analysis = selectedAnalyses[0];
  const analysisSetup = analysis ? getAnalysisSetup(setups, analysis) : null;

  const { displayFilterRemovesAllActiveAnalyses } = useActiveAnalysesWithDisplayFilters(project.id);

  const { chartType, chartTypeDescriptions, chartTypeOptions, setChartTypeOption } =
    useDiagnosisChartType({
      project,
      quantities,
    });
  const [publicationFlagDisplayed] = usePublicationFlagDisplay({
    scope: project.id,
    defaultValue: settings.DISPLAY_PUBLICATION_MARKERS,
  });

  const [period, setPeriod, extremes] = useProjectPeriod({
    source,
    project,
    analyses: activeAnalyses,
    boundaries: "active-analyses",
  });

  const hasDataInPeriod = useHasDiagnosticDataInPeriod(
    activeAnalyses,
    indexedFailureValues,
    period,
  );

  const hasDataInAllPeriod = useHasDiagnosticDataInPeriod(
    activeAnalyses,
    indexedFailureValues,
    extremes,
  );

  const handleZoomToAllPeriod = React.useCallback(
    () => setPeriod({ from: extremes.from, to: extremes.to }),
    [extremes.from, extremes.to, setPeriod],
  );

  const subtitle = useMemo(
    () => (
      <EvolutionSubHeader
        projectIdToFilter={project.id}
        allAnalysesMode={false}
        chartOptions={
          <InfoTooltip
            title={t("diagnosis.overview.header.option.title")}
            options={chartTypeDescriptions}
            link="/faults-page"
          >
            <div className={HIDE_ON_WIDGET_EXPORT}>
              <Selector
                options={chartTypeOptions}
                value={chartType}
                onChange={setChartTypeOption}
              />
            </div>
          </InfoTooltip>
        }
        actionMenu={
          !displayFilterRemovesAllActiveAnalyses && (
            <ActionMenu
              event={null}
              openFailureManagementModal={openModal}
              analyses={activeAnalyses}
              project={project}
              canWriteProject={canUserWriteProject}
              canUserReadProject={canUserReadProject}
              allAnalysesMode={false}
              className={HIDE_ON_WIDGET_EXPORT}
            />
          )
        }
        periodSettings={
          <>
            <PeriodZoomAllButton
              visible={
                !displayFilterRemovesAllActiveAnalyses && !hasDataInPeriod && hasDataInAllPeriod
              }
              onClick={handleZoomToAllPeriod}
              label={t("chart.period.zoom_all")}
            />
            <PeriodSelector
              project={project}
              source={source}
              analyses={activeAnalyses}
              boundaries="active-analyses"
            />
          </>
        }
        visibilitySettings={
          <PeriodMenuDiagnosis
            project={project}
            activeAnalyses={activeAnalyses}
            publicationFlagDisplayed={publicationFlagDisplayed}
          />
        }
      />
    ),
    [
      activeAnalyses,
      canUserReadProject,
      canUserWriteProject,
      chartType,
      chartTypeDescriptions,
      chartTypeOptions,
      displayFilterRemovesAllActiveAnalyses,
      handleZoomToAllPeriod,
      hasDataInAllPeriod,
      hasDataInPeriod,
      openModal,
      project,
      publicationFlagDisplayed,
      setChartTypeOption,
      source,
    ],
  );

  const legendLeft = React.useMemo(
    () =>
      analysisSetup ? (
        <div
          className={cx(commonLegendactiveSetup, HIDE_ON_WIDGET_EXPORT)}
          title={t("project.measure.legend_right.active_setup.name", analysisSetup.name)}
        >
          {t("project.measure.legend_right.active_setup.name", analysisSetup.name)}
        </div>
      ) : null,
    [analysisSetup],
  );

  const title = useMemo(
    () => (
      <>
        <Text as="span" type="h4">
          {t("diagnosis.overview.card.title")}
        </Text>
        <div style={{ marginLeft: "auto" }}>
          <ExportWidgetButton
            fileName={generateExportFileName({
              filename: t("diagnosis.overview.card.title"),
              projectName: project.name,
              extension: "png",
            })}
          />
          <FullViewportTrigger style={{ marginLeft: spacings.xs }} />
        </div>
      </>
    ),
    [project.name],
  );

  if (displayFilterRemovesAllActiveAnalyses) {
    return (
      <ChartCard
        title={title}
        subtitle={subtitle}
        legendLeft={legendLeft}
        className={cx(FailureEvolutionChartCard, FullViewportButtonMarker, exportWidgetTarget)}
      >
        <DisplayFiltersBlankState projectId={project.id} />
      </ChartCard>
    );
  }

  return (
    <>
      <DiagnosisHighcharts
        title={title}
        subtitle={subtitle}
        legendLeft={legendLeft}
        project={project}
        source={source}
        quantities={quantities}
        setups={setups}
        activeAnalyses={activeAnalyses}
        indexedFailureMetadata={indexedFailureMetadata}
        indexedFailureValues={indexedFailureValues}
        indexedManualFailureValues={indexedManualFailureValues}
        analysisSetup={analysisSetup}
      />
      <StyledModal
        title={t("manualFailure.modal.title")}
        isOpen={modal}
        onRequestClose={closeModal}
        size="full"
      >
        <ManualFailuresModalForm
          closeModal={closeModal}
          selectedAnalyses={selectedAnalyses}
          activeAnalyses={activeAnalyses}
          boundary={boundary}
          project={project}
        />
      </StyledModal>
    </>
  );
};

export default React.memo(FailureEvolution);
